import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import {
  provisionReplicaDatabaseApi,
  ProvisionReplicaDatabaseParams,
  ProvisionReplicaDatabaseResponse,
} from '../provisionReplicaDatabaseApi';

/**
 * Mutation hook to provision a replica database in Azure.
 */
export const useProvisionReplicaDatabaseMutation = () => {
  const token = useSelector(selectAuthToken) as string | undefined;
  const { showSuccess, showError } = useNotification();

  const mutation = useMutation<
    AxiosResponse<ProvisionReplicaDatabaseResponse>,
    unknown,
    ProvisionReplicaDatabaseParams
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return provisionReplicaDatabaseApi({ params, token });
    },
    onSuccess: (response) => {
      const { databaseName, status } = response.data;
      showSuccess(`Replica database provisioned: ${databaseName} (${status})`);
    },
    onError: () => {
      showError('Failed to provision replica database');
    },
  });

  return {
    provisionReplicaDatabase: mutation.mutate,
    provisionReplicaDatabaseAsync: mutation.mutateAsync,
    isProvisioningReplicaDatabase: mutation.isPending,
    provisionResult: mutation.data?.data,
  };
};
